import { patchEntityRecord, getEntityRecord } from '@kizen/api/records';
import {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
  useContext,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ClientService from 'services/ClientService';
import { isMobile, useWindowSize } from 'app/spacing';
import { getSearchParam, setSearchParams } from 'hooks/useSearchParam';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import Loader from 'components/Kizen/Loader';
import { useToast, toastVariant } from 'components/ToastProvider';
import useModal from 'components/Modals/useModal';
import { AiChatModal } from 'ts-components/AiChatModal';
import { getOrderingParam, getSortValue } from 'utility/SortingHelpers';
import ContactsListPage from './ListPage';
import ContactsTabularPage from './TabularPage';
import ConfirmActionModal from './ConfirmActionModal';
import PerformActionModal from 'pages/Common/bulkactions/PerformActionModal';
import {
  BULK_ACTION_TO_BULK_PERMISSION,
  getPerformActionOptions,
  getCustomActionOptions,
  getContactsModalGroupDefs,
} from 'pages/Common/bulkactions/model';
import { useTranslation } from 'react-i18next';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { getRecordOverviewAccess } from 'store/authentication/selectors';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFilterVariables } from 'ts-components/filters';
import ConfirmationModal from 'components/Modals/presets/ConfirmationModal';

import {
  buildPage,
  changeGroup,
  clearFilter as clearFilterAction,
  clearFilterSearch,
  createOrUpdateGroup,
  updatePageConfig,
  updatePageConfigBySearch,
  updateContact,
  addNewContact,
  archiveContactSuccess,
  setCheckedContact,
  setCheckedSelection,
  setFilterName,
  setFilter,
  setCountsNeedToLoad,
  setIsAllContactsGroup,
  setPageConfig,
  resetFilter,
  clearGenericToastErrorFlag,
} from 'store/contactsPage/actions';
import {
  getContacts,
  getCounts,
  removeGroup,
} from 'store/contactsPage/actions';
import {
  getContactsPageReady,
  getFilterGroupReason,
  getHasFilterError,
  getIsInitialLoading,
  getIsLoadingView,
  getNeedToLoadCountsSelector,
  selectIsFilterFromUrl,
} from 'store/contactsPage/selectors';
import CreateEntityModalFull, {
  CONTACT,
} from 'components/Modals/CreateEntity/FullForm';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useDebounce } from 'react-use';
import { DEFAULT_DELAY } from 'utility/config';
import { maybeTrackSearch } from 'utility/analytics';
import ControlBar from './ControlBar';
import ObjectCharts from 'components/ObjectCharts';
import { VIEW_VARIANTS } from 'components/ViewVariant/types';
import { DEFAULT_PATHS } from 'routes/paths';
import { deferExecution } from 'utility/defer';
import { DASHBOARD } from 'queries/query-keys';
import {
  createDashboardQuery,
  fetchDashboardsQuery,
} from 'components/AccessRequests/useDashboardList';
import useClientObjectChartGroupConfig from './useContactsChartGroupConfig';
import { getSortedDashboards } from 'components/DashboardGrid/utils';
import { buildSharingSettingsForAPI } from 'utility/sharing';
import CreateLayoutModal from 'components/DashboardGrid/CreateLayoutModal';
import { setNumberOfFilters } from 'store/contactsPage/actions';
import { FilterDropdownContext } from 'pages/Common/components/filterDropdownContext';
import {
  DATA_MANAGER_SESSION_ACTIONS,
  useDataManagerSession,
} from 'app/dataManagerSession';
import { buildPageFilterQuery } from 'ts-filters/utils';
import { useFilterErrorEffect } from 'hooks/useFilterErrorEffect';
import { useRecordsPageRouting } from 'hooks/useRecordsPageRouting';
import { RECORD_LIST_CONFIG_KEYS } from 'services/TeamMemberService';
import { getColumns, shallowEqualList } from 'pages/Common/helpers/table';
import {
  CUSTOMIZE_FIELDS_STEP_KEY,
  GO_BACK_KEYS,
} from 'components/Wizards/CustomObject/constants';
import { useSyncDispatch } from 'ts-components/hooks/useSyncDispatch';
import { flushSync } from 'react-dom';
import { useDefaultModelQuery } from '__queries/models/custom-objects';
import AxiosService from 'services/AxiosService';
import { snakeToCamelCaseKeys } from '__services/helpers';
import { areFiltersEqual } from 'utility/validate';
import { TASK_TYPE, FLOW_TYPE, FLOW_STATUS } from 'ts-components/Conversation';
import { useRecordDetailCustomScript } from '@kizenapps/engine/react';
import { useGeneratedFilterNavigation } from 'hooks/useGeneratedFilterNavigation';
import { PluginContext } from 'ts-components/Plugins/context';
import { useJsActionOverride } from 'ts-components/Plugins/jsActions';

const AI_PROMPT_QUERY_PARAM = 'userQuery';
const FILTER_OPEN_QUERY_PARAM = 'filterOpen';

const IgnoredQueryParams = [AI_PROMPT_QUERY_PARAM];

const SupportedQueryFilters = [
  'automations',
  'dynamictag',
  'form',
  'survey',
  'tag',
  'titles',
];

const defaultPageSettings = {
  pageNumber: 1,
  pageSize: 50,
};

export default function ContactsPage(props) {
  const { t } = useTranslation();
  useSetTitleOnLoad(props.title(t));
  const history = useHistory();
  const hasGroupQueryParam = useRef(
    Boolean(getSearchParam(history.location, 'group'))
  );
  const { width } = useWindowSize();
  const dispatch = useDispatch();
  const syncDispatch = useSyncDispatch();
  const preReleaseFeatures = usePreReleaseFeatures();
  const [showToast] = useToast();
  const { subpage, chartId } = useParams();
  const isChartView = Boolean(subpage === VIEW_VARIANTS.CHARTS);

  // state functions -----------------------------------------------
  const [userActionChoice, setUserActionChoice] = useState(null);
  const [isMenuOpen, toggleMenu] = useState(false);
  const [newContactModal, setNewContactModal] = useState(false);
  // ---------------------------------------------------------------

  // selectors functions -------------------------------------------
  const bulkActionsPermissions = useSelector(({ authentication }) => {
    const { access } = authentication;
    return access.contacts && access.contacts.bulk_actions;
  });
  const isFetching = useSelector((s) => s.contactPage.isFetching);
  const isInitialLoading = useSelector(getIsInitialLoading);
  const categorizedFields = useSelector((s) => s.contactPage.categorizedFields);
  const fields = useSelector((s) => s.contactPage.fields);
  const allFields = useSelector((s) => s.contactPage.allFields);
  const groups = useSelector((s) => s.contactPage.groups);
  const contacts = useSelector((s) => s.contactPage.contacts);
  const contactsCount = useSelector((s) => s.contactPage.contactsCount);
  const showGenericToastError = useSelector(
    (s) => s.contactPage.showGenericToastError
  );
  const isFilterFromUrl = useSelector(selectIsFilterFromUrl);
  // Specifically for count shown in header
  const contactsCountAndNew = useSelector(
    (s) => s.contactPage.contactsCountAndNew
  );
  const groupCount = useSelector((s) => s.contactPage.groupCount);
  const allCount = useSelector((s) => s.contactPage.allCount);
  const pageConfig = useSelector((s) => s.contactPage.pageConfig);
  const isLoadingView = useSelector(getIsLoadingView);
  const selection = useSelector((s) => s.contactPage.selection);
  const filters = useSelector((s) => s.contactPage.filters);
  const hasFilterError = useSelector(getHasFilterError);
  const needToLoadCounts = useSelector(getNeedToLoadCountsSelector);
  const { dispatchSessionState } = useDataManagerSession();
  const clientObject = useSelector((s) => s.contactPage.clientObject);
  const filterGroupReason = useSelector(getFilterGroupReason);
  const pageReady = useSelector(getContactsPageReady);
  const { viewVariantsAccess, quickFiltersAccess } = useSelector(
    getRecordOverviewAccess
  );
  const [lastCheckedSelection, setLastCheckedSelection] = useState(null);
  const [isArchivingContact, setIsArchivingContact] = useState(false);
  const [filterStateDirty, setFilterStateDirty] = useState(false);
  const [changeGroupArgs, setChangeGroupArgs] = useState({});

  const { isLoading: isLoadingPlugins } = useContext(PluginContext);

  const savedColumnSettings = useSelector(
    (s) => getColumns(s.contactPage.pageConfig),
    shallowEqualList
  );

  const lastFetchedCriteria = useSelector((s) => s.contactPage.fetchedCriteria);

  const { search } = pageConfig;

  // Track debounced changes to the search term and send to google analytics
  useDebounce(() => maybeTrackSearch(search, 'contacts'), DEFAULT_DELAY, [
    search,
  ]);

  const clientObjectId = clientObject?.id;

  // memo functions
  const sortValue = useMemo(
    () => getSortValue(pageConfig.sort),
    [pageConfig.sort]
  );

  const contactsGroupDefs = useMemo(() => getContactsModalGroupDefs(t), [t]);

  const performActionOptionsWithPermissions = useMemo(() => {
    const actionOptions = getPerformActionOptions(t, t('Contacts'));
    return actionOptions
      .filter(
        (option) =>
          bulkActionsPermissions?.[BULK_ACTION_TO_BULK_PERMISSION[option.value]]
      )
      .concat(getCustomActionOptions(clientObject?.customActions || [], t));
  }, [bulkActionsPermissions, t, clientObject?.customActions]);

  const [pageConfigGroup, filterGroup] = useMemo(
    () => [
      pageConfig.queryFilter ? null : pageConfig.group,
      pageConfig.queryFilter
        ? undefined
        : (groups || []).find(({ id }) => id === pageConfig.group),
    ],
    [pageConfig.group, pageConfig.queryFilter, groups]
  );

  const businessClientObject = useSelector(getBusinessClientObject);

  const contactQuery = useDefaultModelQuery(businessClientObject.id, {
    enabled: Boolean(businessClientObject.id),
  });
  const contactQueryRef = useRef(contactQuery);
  contactQueryRef.current = contactQuery;

  const allInGroupDisabled = useMemo(() => {
    const matches =
      !lastFetchedCriteria ||
      areFiltersEqual(
        // It seems a little silly to convert both to camel case, but depending on how the data is fetched, one or the other
        // might be snake case, so we just force them both camel case for consistency
        snakeToCamelCaseKeys(filterGroup?.config),
        snakeToCamelCaseKeys(lastFetchedCriteria)
      );

    return !matches;
  }, [filterGroup, lastFetchedCriteria]);

  const selectionOptions = useMemo(() => {
    return !needToLoadCounts
      ? [
          {
            label:
              contacts.length !== 0
                ? `${t('This Page')} (${Number(
                    contacts.length
                  ).toLocaleString()})`
                : t('This Page'),
            value: 'page',
            numberContacts: contacts.length,
          },
          {
            label:
              contacts.length !== 0
                ? `${t('All in Search')} (${Number(
                    contactsCountAndNew
                  ).toLocaleString()})`
                : t('All in Search'),
            value: 'search',
            numberContacts: contactsCountAndNew,
          },
          {
            label:
              groupCount !== 0 && !allInGroupDisabled
                ? `${t('All in Group')} (${Number(
                    pageConfigGroup ? groupCount : allCount
                  ).toLocaleString()})`
                : t('All in Group'),
            value: 'group',
            numberContacts: pageConfigGroup ? groupCount : allCount,
            disabled: allInGroupDisabled,
          },
          {
            label:
              allCount !== 0
                ? `${t('All Contacts')} (${Number(allCount).toLocaleString()})`
                : t('All Contacts'),
            value: 'all',
            numberContacts: allCount,
          },
          {
            label: t('None'),
            value: 'none',
            numberContacts: 0,
          },
        ]
      : [];
  }, [
    contacts,
    groupCount,
    contactsCountAndNew,
    allCount,
    pageConfigGroup,
    needToLoadCounts,
    allInGroupDisabled,
    t,
  ]);

  const aiFilterPromptRef = useRef(
    getSearchParam(history.location, AI_PROMPT_QUERY_PARAM)
  );

  const clearAIPromptSearchParam = useCallback(() => {
    aiFilterPromptRef.current = null;
    const currentParams = new URLSearchParams(history.location.search);
    currentParams.delete(AI_PROMPT_QUERY_PARAM);
    history.replace({
      ...history.location,
      search: currentParams.toString(),
    });
  }, [history]);

  useEffect(() => {
    const aiPromptParam = getSearchParam(
      history.location,
      AI_PROMPT_QUERY_PARAM
    );
    if (aiPromptParam) {
      aiFilterPromptRef.current = aiPromptParam;
      toggleMenu(true);
    }
  }, [history.location]);

  useEffect(() => {
    const sort = getSearchParam(history.location, 'sort');
    const group = getSearchParam(history.location, 'group');
    const search = getSearchParam(history.location, 'q');
    const page = getSearchParam(history.location, 'page');
    const size = getSearchParam(history.location, 'size');
    const filterOpen = getSearchParam(
      history.location,
      FILTER_OPEN_QUERY_PARAM
    );

    const queryFilter = SupportedQueryFilters.reduce((acc, el) => {
      try {
        const data = getSearchParam(history.location, el);
        if (!data) return acc;
        const filter = JSON.parse(data);
        return {
          ...filter,
          queryParam: el,
        };
      } catch (error) {
        return acc;
      }
    }, null);

    const ignoredSearchParams = IgnoredQueryParams.reduce((acc, el) => {
      return {
        ...acc,
        [el]: getSearchParam(history.location, el),
      };
    }, {});

    syncDispatch(
      buildPage({
        history,
        page: {
          search: search || '',
          group,
          sort,
          page: page && parseInt(page, 10),
          size: size && parseInt(size, 10),
          queryFilter,
          viewVariantOverride:
            ['dynamictag', 'titles', 'tag'].includes(queryFilter?.queryParam) &&
            VIEW_VARIANTS.TABLE,
        },
        clientObject: contactQueryRef.current.isStale
          ? null
          : contactQueryRef.current.data,
      })
    );

    if (
      queryFilter ||
      ignoredSearchParams[AI_PROMPT_QUERY_PARAM] ||
      filterOpen
    ) {
      toggleMenu(true);
    }

    setSearchParams(
      history,
      SupportedQueryFilters.reduce(
        (acc, el) => {
          return {
            ...acc,
            [el]: null,
          };
        },
        { [FILTER_OPEN_QUERY_PARAM]: null }
      ),
      { method: 'replace' }
    );
  }, [syncDispatch, history]);

  useEffect(() => {
    if (userActionChoice) {
      // eslint-disable-next-line no-use-before-define
      openPerformModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userActionChoice]);

  const storeViewVariant = useCallback(
    (value) => {
      dispatch(
        updatePageConfig({
          // if filter is applied on page load then default to table view
          viewVariant: value,
        })
      );
      if (!hasGroupQueryParam.current) {
        dispatch(
          setPageConfig({
            updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
          })
        );
      }
    },
    [dispatch]
  );

  const { viewVariant, changeChartGroup } = useRecordsPageRouting(
    !!clientObjectId,
    storeViewVariant,
    pageConfig?.viewVariant,
    {
      viewVariantsAccess,
      isContact: true,
      queryParams: { group: getSearchParam(history.location, 'group') },
    }
  );

  useEffect(() => {
    if (clientObject?.id && pageReady) {
      if (hasGroupQueryParam.current) {
        syncDispatch(clearFilterSearch());
      }
      hasGroupQueryParam.current = false;
      syncDispatch(
        getContacts({
          isInitialPageLoad: true,
          triggerViewLoading: true,
          updatePageConfig: false,
          onlyUpdateCounts: viewVariant === VIEW_VARIANTS.CHARTS,
        })
      );
    }
  }, [syncDispatch, viewVariant, clientObject, pageReady]);

  useEffect(() => {
    if (showGenericToastError) {
      showToast({
        message: t(
          'There was an error loading the contact data. Please try again.'
        ),
        variant: toastVariant.FAILURE,
      });
      dispatch(clearGenericToastErrorFlag());
    }
  }, [showGenericToastError, showToast, t, dispatch]);

  // ---------------------------------------------------------------

  // filter handlers -----------------------------------------------

  const filterInfo = useMemo(
    () => ({
      ...filters,
      name: pageConfig.queryFilter
        ? pageConfig.queryFilter?.maybeName
        : filters.name,
    }),
    [filters, pageConfig.queryFilter]
  );

  const resetFilterToast = useFilterErrorEffect({
    toggleMenu,
    filterGroupReason,
    hasFilterError,
  });

  const updateFilterName = useCallback(
    (name) => {
      dispatch(setFilterName({ name }));
    },
    [dispatch]
  );

  const setFilterByConfig = useCallback(
    (config) => {
      dispatch(setFilter({ filterObject: config }));
    },
    [dispatch]
  );

  const updateFilterCount = useCallback(
    (count) => {
      dispatch(setNumberOfFilters(count));
    },
    [dispatch]
  );

  const applyFiltersAction = useCallback(
    (payload) => {
      dispatchSessionState({
        type: DATA_MANAGER_SESSION_ACTIONS.RESET,
        parentId: clientObjectId,
      });

      dispatch(
        updatePageConfig({
          page: 1,
          filterObject: payload,
        })
      );
      dispatch(
        getContacts({
          updatePageConfig: true,
          updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
          onlyUpdateCounts: viewVariant === VIEW_VARIANTS.CHARTS,
        })
      );
      dispatch(
        setCheckedSelection({
          value: 'none',
        })
      );
    },
    [dispatch, viewVariant, dispatchSessionState, clientObjectId]
  );

  // Apply unsaved filter from AI chat preview navigation context; the filter
  // is applied by ClientFilterDropdown via applyGeneratedFilter once the
  // menu opens, matching the inline AI filter generation flow
  const onGeneratedFilterNavigation = useCallback(() => {
    // Reset filter/group state so the applied filter is in an unsaved state
    dispatch(clearFilterAction());
    toggleMenu(true);
  }, [dispatch]);

  const { generatedFilter, clearGeneratedFilter } =
    useGeneratedFilterNavigation({
      pageReady,
      onApply: onGeneratedFilterNavigation,
    });

  const applyQuickFilters = useCallback(
    (payload) => {
      dispatchSessionState({
        type: DATA_MANAGER_SESSION_ACTIONS.RESET,
        parentId: clientObjectId,
      });
      dispatch(
        updatePageConfig({
          page: 1,
          quickFilters: payload,
        })
      );
      dispatch(
        setCheckedSelection({
          value: 'none',
        })
      );
      dispatch(
        getContacts({
          updatePageConfig: true,
          updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
          onlyUpdateCounts: viewVariant === VIEW_VARIANTS.CHARTS,
        })
      );
    },
    [dispatch, viewVariant, dispatchSessionState, clientObjectId]
  );

  const saveGroupAction = useCallback(
    ({ filterGroup, isPatch }) => {
      dispatchSessionState({
        type: DATA_MANAGER_SESSION_ACTIONS.RESET,
        parentId: clientObjectId,
      });

      dispatch(
        createOrUpdateGroup({
          filterGroup,
          history,
          isPatch,
        })
      );
      dispatch(
        updatePageConfig({
          filterObject: filterGroup.config,
        })
      );
      dispatch(
        setCheckedSelection({
          value: 'none',
        })
      );
    },
    [history, dispatch, dispatchSessionState, clientObjectId]
  );

  const deleteGroupAction = useCallback(() => {
    dispatchSessionState({
      type: DATA_MANAGER_SESSION_ACTIONS.RESET,
      parentId: clientObjectId,
    });

    dispatch(removeGroup({ history }));
    dispatch(
      setCheckedSelection({
        value: 'none',
      })
    );
    toggleMenu(false);
  }, [dispatch, history, dispatchSessionState, clientObjectId]);

  const clearFilter = useCallback(() => {
    dispatchSessionState({
      type: DATA_MANAGER_SESSION_ACTIONS.RESET,
      parentId: clientObjectId,
    });

    dispatch(clearFilterAction());
    dispatch(
      getContacts({
        updatePageConfig: true,
        updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
        onlyUpdateCounts: viewVariant === VIEW_VARIANTS.CHARTS,
      })
    );
    syncDispatch(
      setCheckedSelection({
        value: 'none',
      })
    );
    deferExecution(() => {
      setSearchParams(history, {
        page: null,
        ...(pageConfig.group ? { group: null } : {}),
        ...(pageConfig.search ? { q: '' } : {}),
      });
    });
  }, [
    dispatch,
    history,
    pageConfig.group,
    pageConfig.search,
    viewVariant,
    dispatchSessionState,
    clientObjectId,
    syncDispatch,
  ]);

  // ---------------------------------------------------------------

  // table handlers ------------------------------------------------
  const handleChangeGroupId = useCallback(
    ({ value, refetch }) => {
      const nextGroup = value === 'none' ? null : value;
      // reset error flag
      resetFilterToast();

      dispatchSessionState({
        type: DATA_MANAGER_SESSION_ACTIONS.RESET,
        parentId: clientObjectId,
      });

      dispatch(
        changeGroup({
          id: nextGroup,
          refetch,
          t,
        })
      );
      dispatch(setIsAllContactsGroup(nextGroup === null));
      dispatch(
        setCheckedSelection({
          value: 'none',
        })
      );
      dispatch(setCountsNeedToLoad());
      deferExecution(() => {
        setSearchParams(history, {
          group: nextGroup,
          page: null,
        });
      });
    },
    [
      history,
      dispatch,
      t,
      dispatchSessionState,
      clientObjectId,
      resetFilterToast,
    ]
  );

  const handleResetFilter = useCallback(() => {
    dispatch(resetFilter());
  }, [dispatch]);

  const handleChangeSearch = useCallback(
    (value) => {
      dispatchSessionState({
        type: DATA_MANAGER_SESSION_ACTIONS.RESET,
        parentId: clientObjectId,
      });

      dispatch(
        updatePageConfigBySearch({
          search: value,
          page: defaultPageSettings.pageNumber,
        })
      );
      dispatch(
        setCheckedSelection({
          value: 'none',
        })
      );
      dispatch(
        getContacts({
          updatePageConfig: false,
          onlyUpdateCounts: viewVariant === VIEW_VARIANTS.CHARTS,
        })
      );
      deferExecution(() =>
        setSearchParams(
          history,
          {
            q: value,
            page: null,
          },
          { method: 'replace' }
        )
      );
    },
    [history, dispatch, viewVariant, dispatchSessionState, clientObjectId]
  );

  const handleSortTable = useCallback(
    ({ column, direction }) => {
      const value = getOrderingParam({ column, direction });
      dispatch(
        updatePageConfig({
          sort: value,
        })
      );
      dispatch(
        getContacts({
          updatePageConfig: true,
          updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
        })
      );
      deferExecution(() => {
        setSearchParams(history, {
          sort: value,
        });
      });
    },
    [dispatch, history]
  );

  const handleChangePageNumber = useCallback(
    (value) => {
      dispatch(
        updatePageConfig({
          page: value,
        })
      );
      dispatch(
        getContacts({
          updatePageConfig: true,
          updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
        })
      );
      deferExecution(() => {
        setSearchParams(history, {
          page: value,
        });
      });
    },
    [history, dispatch]
  );

  const handleChangePageSize = useCallback(
    (value) => {
      dispatch(
        updatePageConfig({
          size: value,
        })
      );
      dispatch(
        getContacts({
          updatePageConfig: true,
          updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
        })
      );
      deferExecution(() => {
        setSearchParams(history, {
          size: value,
        });
      });
    },
    [history, dispatch]
  );

  const handleAddContact = useCallback(
    (contact) => {
      dispatch(setCountsNeedToLoad());
      dispatch(addNewContact({ contact }));
      return true;
    },
    [dispatch]
  );

  const handleUpdateContact = useCallback(
    async ({ entityId, values }) => {
      const updates = [];
      const updatedFieldIds = [];

      for (const { field, value } of values) {
        if (field.fieldType === 'relationship') {
          const update = { id: field.id };

          if (Array.isArray(value)) {
            update.value = value.map((v) => v.id);
          } else if (!value) {
            update.value = null;
          } else {
            if (value.id) {
              update.value = { id: value.id };
            }

            if (value.addValues) {
              update.add_values = value.addValues.map((x) => {
                return { id: x.id };
              });
            }

            if (value.removeValues) {
              update.remove_values = value.removeValues.map((x) => {
                return { id: x.id };
              });
            }
          }

          updates.push(update);
        } else {
          updates.push({ id: field.id, value });
        }
        updatedFieldIds.push(field.id);
      }

      const result = await patchEntityRecord(
        AxiosService,
        clientObject.id,
        entityId,
        { fields: updates },
        { params: { return_all_fields: true } }
      );

      dispatch(updateContact({ contact: result, updatedFieldIds }));
      return true;
    },
    [dispatch, clientObject.id]
  );

  const handleChecked = useCallback(
    (id) => {
      dispatch(setCheckedContact({ id }));
    },
    [dispatch]
  );

  const handleCheckedSelection = useCallback(
    (value) => {
      setLastCheckedSelection(value.value);
      dispatch(
        setCheckedSelection({
          value,
        })
      );
    },
    [dispatch]
  );

  if (allInGroupDisabled && lastCheckedSelection === 'group') {
    handleCheckedSelection({ value: 'none' });
  }

  const handleSelectViewContact = useCallback(
    ({ id }) => {
      history.push(`/client/${id}/details`);
    },
    [history]
  );

  const handleSelectArchiveContact = useCallback(
    async ({ id }) => {
      try {
        setIsArchivingContact(true);
        await ClientService.archive(id);
        flushSync(() => {
          showToast({
            variant: toastVariant.SUCCESS,
            message: t('The contact has been successfully archived.'),
          });
        });

        dispatch(setCountsNeedToLoad());
        dispatch(archiveContactSuccess({ id }));
        dispatch(getContacts());
      } finally {
        setIsArchivingContact(false);
      }
    },
    [dispatch, showToast, t]
  );

  const handleUpdateTableRecords = useCallback(() => {
    dispatch(
      getContacts({
        updatePageConfig: true,
        updatePageConfigKey: RECORD_LIST_CONFIG_KEYS.LAYOUT,
      })
    );
  }, [dispatch]);

  const [deleteFilterConfirmationModalProps, , deleteFilterconfirmationModal] =
    useModal({
      handleSubmit: () => {
        setFilterStateDirty(false);
        clearFilter();
        handleChangeGroupId(changeGroupArgs);
      },
    });
  const [aiChatModalProps, , aiChatModal] = useModal();
  const [, , performActionModal] = useModal();
  const [confirmActionModalProps, , confirmActionModal] = useModal({
    handleSubmit: (selectedActionGroup) => {
      handleCheckedSelection(selectedActionGroup);
      performActionModal.show();
    },
    handleHide: () => {
      setUserActionChoice(null);
    },
    processHideOnSubmit: false, // no handleHide on handleSubmit
  });
  const openPerformModal = () => {
    if (allCount === 0 || isArchivingContact) {
      dispatch(getCounts());
    }
    if (!selection.checkedCount && selection.value !== 'all') {
      confirmActionModal.show();
    } else {
      performActionModal.show();
    }
  };

  const choiceAction = useCallback(
    (options) => setUserActionChoice(options),
    []
  );
  const checkFilterDirtyState = useCallback(
    (changeGroupArgs) => {
      if (filterStateDirty) {
        setChangeGroupArgs(changeGroupArgs);
        deleteFilterconfirmationModal.show();
      } else {
        handleChangeGroupId(changeGroupArgs);
      }
    },
    [filterStateDirty, deleteFilterconfirmationModal, handleChangeGroupId]
  );

  const [executeScript, internalState] = useRecordDetailCustomScript({
    objectId: clientObjectId,
    onError: (error) => {
      showToast({
        message:
          error?.message ||
          t('An error occurred while executing the custom script.'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const {
    execute: executeOverride,
    pending: overrideCreatePending,
    indicator: overrideCreateIndicator,
  } = useJsActionOverride(clientObjectId ?? '', {
    jsActions: clientObject?.browserJsActions,
    overrideKey: clientObject?.actionOverrideCreate,
  });

  const handleOpenAddContactModal = useCallback(async () => {
    if (executeOverride) {
      try {
        const newlyCreatedContactId = await executeOverride();
        if (
          newlyCreatedContactId &&
          typeof newlyCreatedContactId === 'string'
        ) {
          try {
            const contact = await getEntityRecord(
              AxiosService,
              clientObject.id,
              newlyCreatedContactId
            );
            if (contact) {
              dispatch(setCountsNeedToLoad());
              dispatch(
                addNewContact({
                  contact: { object_type: clientObject.objectType, ...contact },
                })
              );
              return;
            }
          } catch {
            // Error toasting is handled by useJsActionOverride's onError
          }
        }
        // No record ID returned or fetch failed — just refresh counts/list
        dispatch(setCountsNeedToLoad());
        dispatch(getContacts());
      } catch {
        // Error toasting is handled by useJsActionOverride's onError
      }
      return;
    }
    setNewContactModal(true);
  }, [executeOverride, clientObject, dispatch]);

  const handleChoseSettings = ({ value, scriptConfig }) => {
    if (value === 'contactSettings') {
      history.push({
        pathname: `/custom-objects/${clientObjectId}/settings`,
        search: history.location.search,
        state: {
          from: history.location.pathname,
          fromKey: GO_BACK_KEYS.CONTACTS,
        },
      });
    } else if (value === 'customizeFields') {
      history.push({
        pathname: `/custom-objects/${clientObjectId}/settings`,
        search: history.location.search,
        state: {
          from: history.location.pathname,
          fromKey: GO_BACK_KEYS.CONTACTS,
          focusStepKey: CUSTOMIZE_FIELDS_STEP_KEY,
        },
      });
    } else if (value === 'viewApiDocs') {
      window.open(
        `${import.meta.env.VITE_API_BASE_PATH}/record-docs/${encodeURIComponent(clientObject?.name)}/swagger`,
        '_blank',
        'noopener,noreferrer'
      );
    } else if (scriptConfig) {
      executeScript(scriptConfig.script, scriptConfig.args, scriptConfig);
    }
  };

  const handleOpenSelectionMenu = useCallback(() => {
    if (needToLoadCounts) {
      dispatch(getCounts());
    }
  }, [dispatch, needToLoadCounts]);

  const handleAiChatComplete = useCallback(
    ({ status } = {}) => {
      if (!status || status === FLOW_STATUS.FAILED) {
        showToast({
          message: t('Failed to update Contact, please try again later'),
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Contact object updated successfully in Kizen AI Lab'),
          variant: toastVariant.SUCCESS,
        });
      }
      aiChatModal.hide();
    },
    [aiChatModal, showToast, t]
  );

  const {
    isLoading: chartGroupsLoading,
    data,
    refetch: refetchChartGroups,
  } = useQuery({
    queryKey: [...DASHBOARD.DASHBOARDS, clientObjectId, false],
    queryFn: () => {
      return fetchDashboardsQuery(null, false, clientObjectId, 'chart_group');
    },
    enabled: viewVariant === VIEW_VARIANTS.CHARTS && !!clientObjectId,
  });

  const sortedGroups = useMemo(() => getSortedDashboards(data ?? []), [data]);

  const { loading: configLoading, ...chartGroupConfigProps } =
    useClientObjectChartGroupConfig(
      sortedGroups,
      viewVariant !== VIEW_VARIANTS.CHARTS || !!chartId,
      viewVariant === VIEW_VARIANTS.CHARTS && !!data
    );

  const onHidePerformActionModal = useCallback(() => {
    setUserActionChoice(null);
    performActionModal.hide();
    confirmActionModal.hide();
  }, [performActionModal, confirmActionModal]);

  const onResetSelection = useCallback(() => {
    dispatch(
      setCheckedSelection({
        value: 'none',
      })
    );
  }, [dispatch]);

  const createDashboardMutation = useMutation({
    mutationFn: (result) => {
      return createDashboardQuery(result, clientObjectId, 'chart_group');
    },
    onSuccess: (data) => {
      chartGroupConfigProps.afterCreate(data.id);
      deferExecution(() => {
        history.push(`${DEFAULT_PATHS.CONTACTS}/charts/${data.id}`);
      });
    },
  });

  const handleCreateDashboard = useCallback(
    ({ name, permissions, isPrivate }) => {
      createDashboardMutation.mutate({
        name,
        sharing_settings: {
          ...buildSharingSettingsForAPI(permissions).sharing_settings,
          private: isPrivate,
        },
      });
    },
    [createDashboardMutation]
  );

  const [modalProps, , { show: handleShowModal }] = useModal({
    handleSubmit: handleCreateDashboard,
  });

  const handleClickAddChartGroup = useCallback(() => {
    handleShowModal();
  }, [handleShowModal]);

  const fullFilterObject = useMemo(() => {
    const quickFilters = isFilterFromUrl ? {} : pageConfig.quickFilters;
    const { criteria } = buildPageFilterQuery(
      pageConfig.filterObject,
      quickFilters,
      allFields,
      clientObject,
      pageConfig.quickFilterSettings
    );
    return criteria;
  }, [
    pageConfig.filterObject,
    pageConfig.quickFilters,
    allFields,
    clientObject,
    pageConfig.quickFilterSettings,
    isFilterFromUrl,
  ]);

  const quickFilterConfig = useMemo(
    () => ({
      quickFilteringFacets: pageConfig.quickFilteringFacets,
      quickFilterSettings: pageConfig.quickFilterSettings,
    }),
    [pageConfig.quickFilteringFacets, pageConfig.quickFilterSettings]
  );

  const { variables, isLoading } = useFilterVariables();

  let quickFilters = false;
  if (isFilterFromUrl) {
    quickFilters = {};
  } else if (clientObject?.quickFilteringEnabled && quickFiltersAccess) {
    quickFilters = pageConfig.quickFilters;
  }

  if (isInitialLoading || isLoading || !contactQuery?.data) {
    return <Loader loading />;
  }

  const isLoadingViewProp =
    isLoadingView ||
    (viewVariant === VIEW_VARIANTS.CHARTS &&
      (chartGroupsLoading || configLoading));

  return (
    <>
      <FilterDropdownContext
        key={filterInfo.key}
        variables={variables}
        config={filterInfo.config}
        dirtyState={filterStateDirty}
        setDirtyState={setFilterStateDirty}
      >
        {isMobile(width) ? (
          <ContactsListPage
            contacts={contacts}
            contactsCount={contactsCount}
            contactsCountAndNew={contactsCountAndNew}
            groups={groups}
            groupId={pageConfigGroup}
            onChangeGroupId={checkFilterDirtyState}
            search={pageConfig.search}
            onChangeSearch={handleChangeSearch}
            onResetFilter={handleResetFilter}
            sort={sortValue}
            onChangeSort={handleSortTable}
            pageNumber={pageConfig.page || defaultPageSettings.pageNumber}
            pageSize={pageConfig.size || defaultPageSettings.pageSize}
            onChangePageNumber={handleChangePageNumber}
            onChangePageSize={handleChangePageSize}
            checkedCount={selection.checkedCount}
            choiceAction={choiceAction}
            openAddContactModal={() => setNewContactModal(true)}
            openPerformModal={openPerformModal}
            handleUpdateTableRecords={handleUpdateTableRecords}
            clientObject={clientObject}
            isChartView={isChartView}
            chartGroupConfigProps={{
              ...chartGroupConfigProps,
              refetchChartGroups: refetchChartGroups,
              setCurrentDashboardId: changeChartGroup,
            }}
            fullFilterObject={fullFilterObject}
            isLoadingView={isLoadingViewProp}
            {...props}
          />
        ) : (
          <>
            <ControlBar
              onChoseSettings={handleChoseSettings}
              disabled={isFetching || internalState.pending || isLoadingPlugins}
              isChartView={isChartView}
              chartGroupConfigProps={{
                ...chartGroupConfigProps,
                refetchChartGroups: refetchChartGroups,
                setCurrentDashboardId: changeChartGroup,
                handleClickAddDashboard: handleClickAddChartGroup,
              }}
            />
            {isChartView ? (
              <ObjectCharts
                model={clientObject}
                allFields={allFields}
                groups={groups}
                groupId={pageConfigGroup}
                filterGroup={filterGroup}
                handleChangeGroup={checkFilterDirtyState}
                filterInfo={filterInfo}
                hasFilterError={hasFilterError}
                openMenu={isMenuOpen}
                search={pageConfig.search}
                onChangeSearch={handleChangeSearch}
                onResetFilter={handleResetFilter}
                recordsCountAndNew={contactsCountAndNew}
                chartGroupConfigProps={{
                  ...chartGroupConfigProps,
                  refetchChartGroups: refetchChartGroups,
                  setCurrentDashboardId: changeChartGroup,
                  handleClickAddDashboard: handleClickAddChartGroup,
                }}
                fullFilterObject={fullFilterObject}
                quickFilters={quickFilters}
                aiFilterPrompt={aiFilterPromptRef.current}
                clearAiFilterPrompt={clearAIPromptSearchParam}
                generatedFilter={generatedFilter}
                clearGeneratedFilter={clearGeneratedFilter}
                quickFilterConfig={quickFilterConfig}
                toggleMenu={toggleMenu}
                applyFiltersAction={applyFiltersAction}
                applyQuickFilters={applyQuickFilters}
                updateFilterCount={updateFilterCount}
                saveGroupAction={saveGroupAction}
                deleteGroupAction={deleteGroupAction}
                setFilterName={updateFilterName}
                setFilterByConfig={setFilterByConfig}
                clearFilter={clearFilter}
                preReleaseFeatures={preReleaseFeatures}
                errorsReturned={filterInfo.errors}
                isLoadingView={isLoadingViewProp}
              />
            ) : (
              <ContactsTabularPage
                model={clientObject}
                contacts={contacts}
                contactsCount={contactsCount}
                contactsCountAndNew={contactsCountAndNew}
                setFilterByConfig={setFilterByConfig}
                onAddContact={handleAddContact}
                onSubmitContact={handleUpdateContact}
                aiFilterPrompt={aiFilterPromptRef.current}
                clearAiFilterPrompt={clearAIPromptSearchParam}
                generatedFilter={generatedFilter}
                clearGeneratedFilter={clearGeneratedFilter}
                groups={groups}
                groupId={pageConfigGroup}
                filterGroup={filterGroup}
                fields={fields}
                allFields={allFields}
                categorizedFields={categorizedFields}
                onChangeGroupId={checkFilterDirtyState}
                search={pageConfig.search}
                onChangeSearch={handleChangeSearch}
                onResetFilter={handleResetFilter}
                sort={sortValue}
                onChangeSort={handleSortTable}
                selectionOptions={selectionOptions}
                pageNumber={pageConfig.page || defaultPageSettings.pageNumber}
                pageSize={pageConfig.size || defaultPageSettings.pageSize} // just for first render
                onChangePageSize={handleChangePageSize}
                onChangePageNumber={handleChangePageNumber}
                selection={selection}
                onChecked={handleChecked}
                onCheckedSelection={handleCheckedSelection}
                openAddContactModal={handleOpenAddContactModal}
                overrideCreatePending={overrideCreatePending}
                overrideCreateIndicator={overrideCreateIndicator}
                openPerformModal={openPerformModal}
                filterInfo={filterInfo}
                quickFilters={quickFilters}
                quickFilterConfig={quickFilterConfig}
                fullFilterObject={fullFilterObject}
                applyQuickFilters={applyQuickFilters}
                openMenu={isMenuOpen}
                applyFiltersAction={applyFiltersAction}
                updateFilterCount={updateFilterCount}
                choiceAction={choiceAction}
                performActionOptions={performActionOptionsWithPermissions}
                saveGroupAction={saveGroupAction}
                deleteGroupAction={deleteGroupAction}
                setFilterName={updateFilterName}
                toggleMenu={toggleMenu}
                clearFilter={clearFilter}
                onSelectViewContact={handleSelectViewContact}
                onSelectArchiveContact={handleSelectArchiveContact}
                handleUpdateTableRecords={handleUpdateTableRecords}
                onOpenSelectionMenu={handleOpenSelectionMenu}
                data-qa="clients-table"
                errorsReturned={filterInfo.errors}
                isLoadingView={isLoadingViewProp}
                {...props}
              />
            )}
          </>
        )}
      </FilterDropdownContext>
      <ConfirmActionModal
        selectionOptions={selectionOptions.filter(
          (item) => item.value !== 'none'
        )}
        onMenuOpen={handleOpenSelectionMenu}
        {...confirmActionModalProps}
      />
      {performActionModal.showing && (
        <PerformActionModal
          allCount={allCount}
          selection={selection}
          search={pageConfig.search}
          groupId={pageConfigGroup}
          groupOptions={groups}
          onHide={onHidePerformActionModal}
          selectedAction={userActionChoice}
          show={performActionModal.showing}
          onSelect={choiceAction}
          performActionOptions={performActionOptionsWithPermissions}
          groupDefs={contactsGroupDefs}
          filters={fullFilterObject}
          model={clientObject}
          // additional props
          recordsCount={contactsCountAndNew}
          records={contacts}
          onResetSelection={onResetSelection}
          savedColumnSettings={savedColumnSettings}
          fields={fields}
          // onUpdateClientsList added so that clients list can get refreshed on tag change
          // TODO: maybe add redux action which will trigger clients list refresh with the same params?
        />
      )}
      {
        <ConfirmationModal
          buttonText={t('Discard Changes')}
          heading={t('You Have Unsaved Changes')}
          actionBtnColor="red"
          {...deleteFilterConfirmationModalProps}
          data-qa="clear-confirm-modal"
        >
          {t(
            'Unsaved filter group changes will be lost, would you like to continue?'
          )}
        </ConfirmationModal>
      }
      {newContactModal ? (
        <CreateEntityModalFull
          callRecordsEndpoint
          objectType={CONTACT}
          objectId={clientObjectId}
          show={true}
          onCreated={(v) => {
            handleAddContact({
              object_type: clientObject.objectType,
              ...v,
            });
          }}
          onCancel={() => setNewContactModal(false)}
        />
      ) : null}
      <AiChatModal
        {...aiChatModalProps}
        entityName={t('Contact')}
        heading={t('Edit Contact Object in Kizen AI Lab')}
        objectId={clientObjectId}
        context={{
          custom_object_id: clientObjectId,
        }}
        flowType={FLOW_TYPE.custom_object_editor}
        taskType={TASK_TYPE.CUSTOM_OBJECT_EDITOR_PLANNER}
        onConfirm={handleAiChatComplete}
        onError={() => aiChatModal.hide()}
        pendingMessages={[
          t('Analyzing your requirements'),
          t('Updating your contact object schema'),
          t('Refining your contact object fields'),
          t('Finalizing setup'),
        ]}
      />
      <CreateLayoutModal
        {...modalProps}
        canUserEditPermissions
        showEditAdminOptions
        entity={t('Chart Group')}
      />
    </>
  );
}
